// src/portal-app/team/RoleRows.js
//
// The READ path on the Roles + Users surface — how one role and one user
// credential render.
//
// FIELD MAPPING — RoleRow (migrated to CondensedRow, spec §5 P4; brief:
// "role name, assigned-user count as secondary, StatusBadge"):
//   icon       ShieldIcon (CondensedRow always draws the 32px square; a row
//              with no icon prop wastes that space on nothing, same defect
//              TransactionsScreen.js's file header calls out)
//   tone       green for the default role, muted for a custom one — the
//              same tone drives both the icon square and the pill below
//   name       role.name
//   secondary  "{count} users assigned" — role.user_count, the field the
//              brief calls out. role.description is DROPPED: the brief's
//              field list is explicit and does not include it (same
//              disposition EmployeesScreen.js gave employee pay — see that
//              file's header for the precedent). Flagged in the task report.
//   pill       Default / Custom — CondensedRow's pill IS this kit's
//              status-badge slot (no separate node slot exists for a
//              StatusBadge element once a row is migrated — see
//              ReservationRows.js's file header for the same call). Shown
//              on EVERY row, not just default ones, so colour is never the
//              sole carrier of meaning (spec §9): a muted "Custom" pill is
//              still legible text, not just an absent badge.
//   actions    edit / delete, rendered as a sibling row beneath the
//              CondensedRow (same placement as TransactionRow's void
//              button and ReservationRow's Seat/Cancel/No-Show group) —
//              default-role protection (edit disabled, delete absent) is
//              UNCHANGED from the pre-migration screen; see
//              RolesScreen.test.js's mutation-evidence section for proof
//              these guards still fail their tests when broken.
//
// UserRow — NOT migrated to CondensedRow. Three independent actions (edit,
// reset password, deactivate/reactivate) plus a self-protection guard on
// the last one have no slot in CondensedRow's single-pill, single-secondary
// contract; migrating would mean dropping one of the three actions or
// rewriting the self-protection assertions this task must leave provably
// intact. Same disposition TransactionsScreen.js gave the Voids tab (see
// that file's header) — kept as the pre-migration custom card, unchanged
// in shape (DOM structure, all three actions, self-protection guard),
// only relocated out of RolesScreen.js.
//
// ONE DELIBERATE CHANGE, not a shape change: the avatar's colour palette.
// The pre-split screen picked from a private 6-entry `AVATAR_VARS` array
// (including `--c-red`) via `charCodeAt(0) % 6`. Replaced here with
// `avatarTone()` below, which uses the shared `FALLBACK_TONES` (5 tones,
// no red — red is reserved for destructive/error states elsewhere in the
// kit) and `toneVar()`, the same formula EmployeesScreen.js's avatarTone()
// uses. This makes a user's avatar colour consistent with every other
// tone-from-name avatar in the app, at the cost of the colour a given name
// resolves to shifting (mod 5 instead of mod 6) — cosmetic only, no field,
// payload or testid changed. Disclosed in the task report.
import React from 'react';
import {
    CondensedRow, StatusBadge, FALLBACK_TONES, toneVar, IconEdit,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import {
    ShieldIcon, TrashIcon, KeyIcon, UserCheckIcon, initials,
} from './rolesShared';

function avatarTone(name = '') {
    return FALLBACK_TONES[(name.charCodeAt(0) || 0) % FALLBACK_TONES.length];
}

/* ------------------------------------------------------------------ */
/* RoleRow                                                             */
/* ------------------------------------------------------------------ */
export function RoleRow({
    role, onPress, onEdit, onDelete, t,
}) {
    const tone = role.is_default ? 'green' : 'muted';
    const pillLabel = role.is_default
        ? t('default_badge', {}, 'Default')
        : t('custom_role', {}, 'Custom');
    const secondary = t(
        'n_users_assigned',
        { count: role.user_count ?? 0 },
        '{count} users assigned',
    );

    return (
        <div className="rl-role-wrap">
            <CondensedRow
                icon={<ShieldIcon />}
                tone={tone}
                name={role.name}
                secondary={secondary}
                pill={{ label: pillLabel, tone }}
                onPress={onPress}
                testId={role.id}
            />
            <div className="rl-role-actions">
                <button
                    type="button"
                    className="rl-icon-btn"
                    data-testid={`edit-role-${role.id}`}
                    aria-label={t('edit_role', {}, 'Edit Role')}
                    disabled={role.is_default}
                    onClick={() => { if (!role.is_default) onEdit(); }}
                >
                    <IconEdit />
                </button>
                {!role.is_default && (
                    <button
                        type="button"
                        className="rl-icon-btn rl-icon-btn-danger"
                        data-testid={`delete-role-${role.id}`}
                        aria-label={t('delete_role', {}, 'Delete Role')}
                        onClick={onDelete}
                    >
                        <TrashIcon />
                    </button>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* UserRow — unchanged custom card (see file header)                   */
/* ------------------------------------------------------------------ */
export function UserRow({
    cred, roleName, isSelf, deactivating, onEdit, onResetPassword, onToggleActive, t,
}) {
    return (
        <GlassCard
            lift
            className="rl-user-card"
            data-testid="user-card"
            data-cred-id={cred.id}
        >
            <div className="rl-user-header">
                <div
                    className="rl-user-avatar"
                    style={{ background: `var(${toneVar(avatarTone(cred.display_name || cred.username))})` }}
                    aria-hidden="true"
                >
                    {initials(cred.display_name || cred.username)}
                </div>
                <div className="rl-user-info">
                    <div className="rl-user-name">{cred.display_name}</div>
                    <div className="rl-user-username">@{cred.username}</div>
                </div>
            </div>
            <div className="rl-user-footer">
                <StatusBadge
                    status={cred.is_active ? 'active' : 'inactive'}
                    label={cred.is_active ? t('active', {}, 'Active') : t('inactive', {}, 'Inactive')}
                    tone={cred.is_active ? 'green' : 'muted'}
                />
                <span className="rl-user-role">{roleName}</span>
                <div className="rl-user-actions">
                    {/* Edit user */}
                    <button
                        type="button"
                        className="rl-icon-btn"
                        data-testid={`edit-user-${cred.id}`}
                        aria-label={t('edit_user', {}, 'Edit User')}
                        onClick={onEdit}
                    >
                        <IconEdit />
                    </button>
                    {/* Reset password */}
                    <button
                        type="button"
                        className="rl-icon-btn"
                        data-testid={`reset-password-${cred.id}`}
                        aria-label={t('reset_password', {}, 'Reset Password')}
                        onClick={onResetPassword}
                    >
                        <KeyIcon />
                    </button>
                    {/* Deactivate / reactivate — disabled for self */}
                    <button
                        type="button"
                        className="rl-icon-btn rl-icon-btn-danger"
                        data-testid={`toggle-active-${cred.id}`}
                        aria-label={
                            isSelf
                                ? t('cannot_deactivate_self', {}, 'Cannot deactivate your own account')
                                : cred.is_active
                                    ? t('deactivate_user', {}, 'Deactivate')
                                    : t('reactivate_user', {}, 'Reactivate')
                        }
                        disabled={isSelf || deactivating}
                        onClick={() => { if (!isSelf) onToggleActive(); }}
                    >
                        <UserCheckIcon />
                    </button>
                </div>
            </div>
        </GlassCard>
    );
}
